/*
  a11y-fixes.js  (added 2026-10-26, accessibility remediation epic; v16 2026-10-26)
  Small, self-contained accessibility layer loaded after bundle.js.
  Does NOT modify bundle.js. Remove this file + its <script> tag in layout/theme.liquid to roll back.
  Version check: open the browser console (F12 > Console) - it logs "[a11y-fixes] v16 loaded",
  or type  window.A11Y_FIXES_VERSION  in the console.

  1. Live region: announces cart changes to screen readers (quantity/total read from /cart.js).
  2. Cart drawer: dialog role + name, "Close cart", no 'complementary landmark' / empty 'alert',
     click-handler wrappers role=none, "You may also like" blocks are named regions.
  3. Focus management for cart quantity changes, removals and adding from cart recommendations.
  4. Icons: hides decorative SVG icon titles ("Trash", "Error", "Chevron", "Plus") when the control has a name.
  5. Carousel arrows without a name get "Previous" / "Next".
  6. Variant pickers: radio group semantics, checked state, ", sold out"; focus kept after selection.
  7. "More payment options" exposed as a button "More payment options, opens checkout".
  8. Accordions (product details, FAQs, cart discount breakdown): button + aria-expanded + aria-controls,
     Space toggles like Enter, collapsed content is inert.
  9. (v16) Product gallery: thumbnail arrows "Previous image" / "Next image"; thumbnails are buttons
     "Show image N of M" with aria-current on the image shown; Space works like Enter.
     Images whose alt text is a theme colour code (e.g. "#color_white", used by the theme to filter
     images per colour - must NOT be changed in Shopify) get a readable name via aria-label,
     e.g. "The Smart Garden 3, White". The alt attribute itself is left untouched.
*/
(function () {
  'use strict';

  var VERSION = 'v16';
  window.A11Y_FIXES_VERSION = VERSION;
  try { console.info('[a11y-fixes] ' + VERSION + ' loaded'); } catch (e) { /* ignore */ }

  var LIVE_ID = 'A11yLiveRegion';
  var SETTLE_MS = 500;      // wait this long after the last DOM change before acting
  var RESTORE_MS = 2000;    // keep focus on the used control for this long after a change
  var CART_WAIT_MS = 5000;  // max time to wait for Shopify's cart to reflect a change
  var CLOSE_CART_LABEL = 'Close cart';

  function txt(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function isVisible(el) {
    return !!(el && el.getClientRects().length);
  }

  function liveRegion() {
    var el = document.getElementById(LIVE_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = LIVE_ID;
      el.className = 'visually-hidden';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('aria-atomic', 'true');
      document.body.appendChild(el);
    }
    return el;
  }

  function announce(msg) {
    var el = liveRegion();
    el.textContent = '';
    setTimeout(function () { el.textContent = msg; }, 120);
  }

  /* Read the current cart from Shopify (always up to date). */
  function getCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  function findLine(cart, variantId) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === String(variantId)) return cart.items[i];
    }
    return null;
  }

  // Poll /cart.js until it reflects the user's change (or give up after CART_WAIT_MS).
  function waitForCart(p) {
    var start = Date.now();
    return new Promise(function (resolve) {
      (function poll() {
        getCart().then(function (cart) {
          var done = true;
          if (p.type === 'qty') {
            var line = findLine(cart, p.variant);
            done = !line || line.quantity !== p.prevQty;
          } else if (p.type === 'remove') {
            done = !findLine(cart, p.variant);
          }
          if (done || Date.now() - start > CART_WAIT_MS) resolve(cart);
          else setTimeout(poll, 300);
        }).catch(function () {
          if (Date.now() - start > CART_WAIT_MS) resolve(null);
          else setTimeout(poll, 300);
        });
      })();
    });
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: currency || 'USD'
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + (currency || '');
    }
  }

  function focusIsLost() {
    var a = document.activeElement;
    return !a || a === document.body || a === document.documentElement;
  }

  /* ---------- 4. Icons ---------- */
  var CONTROL_SEL = 'button, a[href], [role="button"], [role="radio"], [tabindex]:not([tabindex="-1"])';

  function controlHasName(ctrl) {
    if (ctrl.getAttribute('aria-label') || ctrl.getAttribute('aria-labelledby')) return true;
    var visible = (ctrl.innerText || '').replace(/\s+/g, ' ').trim();
    return visible.length > 0;
  }

  function fixIcons() {
    var svgs = document.querySelectorAll('svg:not([data-a11y-done])');
    for (var i = 0; i < svgs.length; i++) {
      var svg = svgs[i];
      svg.setAttribute('data-a11y-done', '');
      var ctrl = svg.closest(CONTROL_SEL);
      if (!ctrl || controlHasName(ctrl)) {
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
      }
    }
  }

  /* ---------- 5. Carousel arrows ---------- */
  function labelArrows() {
    var els = document.querySelectorAll('[data-direction]:not([aria-label])');
    for (var i = 0; i < els.length; i++) {
      var b = els[i];
      var focusable = b.tagName === 'BUTTON' || b.hasAttribute('tabindex') || b.getAttribute('role') === 'button';
      if (!focusable) continue;
      var dir = b.getAttribute('data-direction');
      if (dir === 'prev') b.setAttribute('aria-label', 'Previous');
      else if (dir === 'next') b.setAttribute('aria-label', 'Next');
      else continue;
      if (b.tagName !== 'BUTTON' && !b.getAttribute('role')) b.setAttribute('role', 'button');
      var svgs = b.querySelectorAll('svg');
      for (var j = 0; j < svgs.length; j++) {
        svgs[j].setAttribute('aria-hidden', 'true');
        svgs[j].setAttribute('focusable', 'false');
      }
    }
  }

  /* ---------- 6. Variant pickers ---------- */
  function fixVariantPickers() {
    var groups = document.querySelectorAll('[data-variant-picker-option-labels]');
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      var labels = group.querySelectorAll('label[for]');
      if (!labels.length) continue;
      var groupName = group.getAttribute('option-picker-name') || 'Options';
      if (group.getAttribute('role') !== 'radiogroup') group.setAttribute('role', 'radiogroup');
      if (group.getAttribute('aria-label') !== groupName) group.setAttribute('aria-label', groupName);

      for (var i = 0; i < labels.length; i++) {
        var label = labels[i];
        var input = document.getElementById(label.htmlFor);
        var value = input ? input.value : txt(label);
        var soldOut = label.hasAttribute('no-available') ||
          /unavailable|sold-out|soldout|disabled/i.test(label.className) ||
          (input && input.disabled);
        var selected = label.classList.contains('selected') || (input && input.checked);
        var name = value + (soldOut ? ', sold out' : '');

        if (label.getAttribute('role') !== 'radio') label.setAttribute('role', 'radio');
        var checked = selected ? 'true' : 'false';
        if (label.getAttribute('aria-checked') !== checked) label.setAttribute('aria-checked', checked);
        if (label.getAttribute('aria-label') !== name) label.setAttribute('aria-label', name);

        var media = label.querySelectorAll('img, svg');
        for (var m = 0; m < media.length; m++) {
          media[m].setAttribute('aria-hidden', 'true');
          if (media[m].tagName === 'IMG' && media[m].getAttribute('alt') !== '') media[m].setAttribute('alt', '');
        }
      }
    }
    // The real radio inputs sit in an invisible (0x0) box and duplicate the visible choices.
    var dupes = document.querySelectorAll('[data-variant-picker-option-inputs]:not([aria-hidden])');
    for (var d = 0; d < dupes.length; d++) dupes[d].setAttribute('aria-hidden', 'true');
  }

  // Remember which picker group the user last used, so focus can be returned there after a redraw.
  var pickerFocus = null; // { section, group, until }

  function restorePickerFocus() {
    if (!pickerFocus || Date.now() > pickerFocus.until) { pickerFocus = null; return; }
    if (!focusIsLost()) return;
    var sel = '[data-variant-picker-option-labels][option-picker-name="' + pickerFocus.group.replace(/"/g, '\\"') + '"]';
    var scope = (pickerFocus.section && document.getElementById(pickerFocus.section)) || document;
    var group = scope.querySelector(sel) || document.querySelector(sel);
    if (!group) return;
    var target = group.querySelector('[role="radio"][aria-checked="true"]') || group.querySelector('label[for]');
    if (target) {
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '0');
      target.focus();
    }
  }

  // Selection changes only toggle classes, so re-sync shortly after any interaction with a picker.
  function scheduleVariantSync(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var group = t.closest('[data-variant-picker-option-labels]');
    if (!group && !t.closest('variant-picker')) return;
    if (group && (e.type === 'click' || e.key === 'Enter' || e.key === ' ')) {
      var section = t.closest('[id^="shopify-section"]');
      pickerFocus = {
        section: section ? section.id : null,
        group: group.getAttribute('option-picker-name') || '',
        until: Date.now() + 2500
      };
    }
    [60, 400, 900, 1600].forEach(function (ms) {
      setTimeout(function () {
        try { fixVariantPickers(); restorePickerFocus(); } catch (err) { /* never break the page */ }
      }, ms);
    });
  }
  document.addEventListener('click', scheduleVariantSync, true);
  document.addEventListener('keyup', scheduleVariantSync, true);

  /* ---------- 7. More payment options ---------- */
  function fixMorePayment() {
    var links = document.querySelectorAll('.shopify-payment-button__more-options:not([data-a11y-done])');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      a.setAttribute('data-a11y-done', '');
      a.setAttribute('role', 'button');
      a.setAttribute('aria-label', (txt(a) || 'More payment options') + ', opens checkout');
      a.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); this.click(); }
      });
    }
  }

  /* ---------- 8. Accordions ---------- */
  var accCounter = 0;

  function fixAccordions() {
    var comps = document.querySelectorAll('accordeon-component');
    for (var i = 0; i < comps.length; i++) {
      var comp = comps[i];
      var btn = comp.querySelector('accordeon-button');
      if (!btn || btn.closest('accordeon-component') !== comp) continue;
      var wrap = comp.querySelector('[data-accordeon-content-wrap]');
      var state = comp.getAttribute('data-aria-expanded');

      if (btn.getAttribute('role') !== 'button') btn.setAttribute('role', 'button');
      if (!btn.hasAttribute('tabindex')) btn.setAttribute('tabindex', '0');

      var svgs = btn.querySelectorAll('svg');
      for (var s = 0; s < svgs.length; s++) {
        svgs[s].setAttribute('aria-hidden', 'true');
        svgs[s].setAttribute('focusable', 'false');
      }

      // Only mirror the state when the theme provides it (never hide content we can't track).
      if (state !== 'true' && state !== 'false') continue;
      if (btn.getAttribute('aria-expanded') !== state) btn.setAttribute('aria-expanded', state);

      if (wrap) {
        if (!wrap.id) wrap.id = 'A11yAccordion-' + (++accCounter);
        if (btn.getAttribute('aria-controls') !== wrap.id) btn.setAttribute('aria-controls', wrap.id);
        // Collapsed content is only squeezed to 0 height by the theme; make it truly unavailable.
        if (state === 'true') {
          if (wrap.hasAttribute('inert')) wrap.removeAttribute('inert');
        } else if (!wrap.hasAttribute('inert')) {
          wrap.setAttribute('inert', '');
        }
      }
    }
  }

  /* ---------- 9. Product gallery + colour-code alt texts ---------- */
  function productNameFor(el) {
    var card = el.closest('vertical-product-card, horizontal-product-card, [class*="product-card"]');
    if (card) {
      var t = card.querySelector('[class*="__title"]');
      if (t && txt(t)) return txt(t);
    }
    var h1 = document.querySelector('main h1');
    return h1 ? txt(h1) : '';
  }

  // "#color_white" -> "White", "#color_sage-green" -> "Sage green"
  function colourFromCode(code) {
    var c = code.replace(/^#/, '');
    var parts = c.split('_');
    var value = parts.length > 1 ? parts.slice(1).join(' ') : parts[0];
    value = value.replace(/[-_]+/g, ' ').trim();
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  function fixColourCodeAlts() {
    var imgs = document.querySelectorAll('img[alt^="#"]:not([data-a11y-alt])');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      img.setAttribute('data-a11y-alt', '');
      // Swatches / thumbnails are named by their control; hide their image entirely.
      if (img.closest('[data-variant-picker-option-labels], .product-thumbnail-carousel__thumb')) {
        img.setAttribute('aria-hidden', 'true');
        continue;
      }
      var name = productNameFor(img);
      var colour = colourFromCode(img.getAttribute('alt'));
      if (name) {
        img.setAttribute('aria-label', name + (colour ? ', ' + colour : ''));
      } else {
        img.setAttribute('aria-hidden', 'true');
      }
    }
  }

  function fixGallery() {
    // Thumbnail arrows
    var arrows = document.querySelectorAll('.product-thumbnail-carousel__btn[data-direction]');
    for (var a = 0; a < arrows.length; a++) {
      var label = arrows[a].getAttribute('data-direction') === 'prev' ? 'Previous image' : 'Next image';
      if (arrows[a].getAttribute('aria-label') !== label) arrows[a].setAttribute('aria-label', label);
    }
    // Thumbnails, per gallery
    var galleries = document.querySelectorAll('.product-thumbnail-carousel, [class*="product-thumbnail-carousel"]:not(.product-thumbnail-carousel__thumb):not(.product-thumbnail-carousel__btn)');
    var seen = [];
    for (var g = 0; g < galleries.length; g++) {
      var thumbsAll = galleries[g].querySelectorAll('.product-thumbnail-carousel__thumb');
      if (!thumbsAll.length) continue;
      var thumbs = [];
      for (var t = 0; t < thumbsAll.length; t++) {
        if (seen.indexOf(thumbsAll[t]) === -1 && isVisible(thumbsAll[t])) thumbs.push(thumbsAll[t]);
        seen.push(thumbsAll[t]);
      }
      for (var k = 0; k < thumbs.length; k++) {
        var th = thumbs[k];
        var name = 'Show image ' + (k + 1) + ' of ' + thumbs.length;
        if (th.getAttribute('role') !== 'button') th.setAttribute('role', 'button');
        if (th.getAttribute('aria-label') !== name) th.setAttribute('aria-label', name);
        var current = th.classList.contains('is-primary');
        if (current && th.getAttribute('aria-current') !== 'true') th.setAttribute('aria-current', 'true');
        if (!current && th.hasAttribute('aria-current')) th.removeAttribute('aria-current');
        var imgs = th.querySelectorAll('img, svg');
        for (var m = 0; m < imgs.length; m++) imgs[m].setAttribute('aria-hidden', 'true');
      }
    }
  }

  // Space should work like Enter on accordion headers and gallery thumbnails.
  document.addEventListener('keydown', function (e) {
    if (e.key !== ' ' && e.key !== 'Spacebar') return;
    var t = e.target;
    if (!t || !t.matches) return;
    if (t.matches('accordeon-button')) {
      e.preventDefault();
      t.click();
    } else if (t.matches('.product-thumbnail-carousel__thumb')) {
      e.preventDefault();
      t.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true }));
      setTimeout(fixGallery, 300);
    }
  }, true);
  document.addEventListener('keyup', function (e) {
    if (e.key === 'Enter' && e.target && e.target.matches && e.target.matches('.product-thumbnail-carousel__thumb')) {
      setTimeout(fixGallery, 300);
    }
  }, true);

  /* ---------- 2. Cart drawer semantics ---------- */
  // Wrappers that carry the theme's click handlers; Chrome exposes them as "section, clickable".
  var PRESENTATIONAL_WRAPPERS = 'quantity-btn, cart-remove-button, cart-item, sidebar-button, ' +
    'vertical-product-card, horizontal-product-card, cart-drawer-items-block';

  // Blocks that list recommended products; named after their heading so Tab users hear the context.
  var RECOMMENDATION_BLOCKS = 'cart-drawer-related-products-block';
  var regionCounter = 0;

  function cartDialog() {
    var d = document.getElementById('CartDrawer');
    if (d && !d.hasAttribute('tabindex')) d.setAttribute('tabindex', '-1');
    return d;
  }

  function labelRecommendationRegions() {
    var blocks = document.querySelectorAll(RECOMMENDATION_BLOCKS);
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      var heading = block.querySelector('h1, h2, h3, h4, h5, h6');
      if (!heading || !txt(heading)) continue;
      if (!heading.id) heading.id = 'A11yRegionHeading-' + (++regionCounter);
      if (block.getAttribute('role') !== 'region') block.setAttribute('role', 'region');
      if (block.getAttribute('aria-labelledby') !== heading.id) block.setAttribute('aria-labelledby', heading.id);
    }
  }

  function fixCartDrawer() {
    var d = document.getElementById('CartDrawer');
    if (d) {
      if (!d.getAttribute('role')) d.setAttribute('role', 'dialog');
      if (!d.getAttribute('aria-label')) d.setAttribute('aria-label', 'Cart');
      var aside = d.closest('aside');
      if (aside && aside.getAttribute('role') !== 'none') aside.setAttribute('role', 'none');
      // Name the close button after what it closes ("Close cart", not just "Close").
      var close = d.querySelector('[data-sidebar-button]');
      if (close && close.getAttribute('aria-label') !== CLOSE_CART_LABEL) close.setAttribute('aria-label', CLOSE_CART_LABEL);
    }
    var wrappers = document.querySelectorAll(PRESENTATIONAL_WRAPPERS);
    for (var w = 0; w < wrappers.length; w++) {
      var wr = wrappers[w];
      // Only when the wrapper itself is not the focusable control (the real <button>/<a> is inside).
      if (wr.getAttribute('role') !== 'none' && !wr.hasAttribute('tabindex')) wr.setAttribute('role', 'none');
    }
    labelRecommendationRegions();
    // The outer notification box is always present (usually empty) and was announced as "alert".
    // The inner visually-hidden span keeps role=alert, so real error messages are still announced.
    var alerts = document.querySelectorAll('.notification__alert[role="alert"]');
    for (var i = 0; i < alerts.length; i++) alerts[i].removeAttribute('role');
  }

  /* ---------- 1 + 3. Cart actions: announcements and focus ---------- */
  var pending = null;     // action waiting for the redraw to finish
  var restore = null;     // { kind, variant, action, until } - keep focus here for a short time
  var settleTimer = null;
  var lastUserTabAt = 0;
  var lastQtyButtonAt = 0;

  function itemQty(item) {
    var q = parseInt(item.getAttribute('data-quantity'), 10);
    if (isNaN(q)) {
      var inp = item.querySelector('input[data-quantity-input]');
      q = inp ? parseInt(inp.value, 10) : NaN;
    }
    return isNaN(q) ? null : q;
  }

  // Name of the product an "Add to cart" button belongs to.
  function addedProductName(btn) {
    var al = btn.getAttribute('aria-label') || '';
    var m = al.match(/^[^,]*,\s*(.+)$/);           // "Add to cart, Basil Plant Pods 3-pack"
    if (m) return m[1];
    var card = btn.closest('vertical-product-card, horizontal-product-card, [class*="product-card"]');
    if (card) {
      var title = card.querySelector('[class*="__title"]');
      if (title && txt(title)) return txt(title);
    }
    return txt(document.querySelector('main h1'));
  }

  function cartCloseButton() {
    var close = document.querySelector('#CartDrawer [data-sidebar-button]');
    if (close && close.getAttribute('aria-label') !== CLOSE_CART_LABEL) close.setAttribute('aria-label', CLOSE_CART_LABEL);
    return close;
  }

  function findRestoreTarget(r) {
    if (!r) return null;
    if (r.kind === 'qty') {
      var item = document.querySelector('cart-item[data-variant-id="' + r.variant + '"]');
      if (!item) return null;
      if (r.action === 'input') return item.querySelector('input[data-quantity-input]');
      return item.querySelector('quantity-btn[data-action="' + r.action + '"] button');
    }
    if (r.kind === 'remove') {
      return document.querySelector('cart-item cart-remove-button button') || cartCloseButton();
    }
    if (r.kind === 'close') {
      return cartCloseButton();
    }
    return null;
  }

  function applyRestore() {
    if (!restore) return;
    if (Date.now() > restore.until) { restore = null; return; }
    var target = findRestoreTarget(restore);
    if (target && document.activeElement !== target) target.focus();
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab') { lastUserTabAt = Date.now(); restore = null; pickerFocus = null; }
  }, true);

  // If the theme moves focus elsewhere in the cart shortly after a change, put it back.
  document.addEventListener('focusin', function (e) {
    // Make sure a (re-rendered) close button already carries its full name when it receives focus.
    if (e.target && e.target.matches && e.target.matches('#CartDrawer [data-sidebar-button]') &&
        e.target.getAttribute('aria-label') !== CLOSE_CART_LABEL) {
      e.target.setAttribute('aria-label', CLOSE_CART_LABEL);
    }
    if (!restore) return;
    if (Date.now() > restore.until) { restore = null; return; }
    if (Date.now() - lastUserTabAt < 300) return; // user is navigating themselves
    var t = e.target;
    var target = findRestoreTarget(restore);
    if (target && t !== target && t.closest && t.closest('[data-sidebar-button], #CartDrawer, cart-item')) {
      setTimeout(function () { if (restore) applyRestore(); }, 0);
    }
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (t.closest('.product-thumbnail-carousel__thumb')) setTimeout(fixGallery, 300);

    var qBtn = t.closest('quantity-btn');
    var item = t.closest('cart-item');

    if (qBtn && item) {
      lastQtyButtonAt = Date.now();
      pending = {
        type: 'qty',
        action: qBtn.getAttribute('data-action'),
        variant: item.getAttribute('data-variant-id'),
        prevQty: itemQty(item)   // read before the theme handles the click (capture phase)
      };
      restore = { kind: 'qty', action: pending.action, variant: pending.variant, until: Date.now() + RESTORE_MS + SETTLE_MS };
      return;
    }

    if (qBtn && !item) {
      // Product page quantity: announce the new value.
      var comp = qBtn.closest('quantity-component');
      setTimeout(function () {
        var inp = comp && comp.querySelector('input');
        if (inp) announce('Quantity ' + inp.value);
      }, 80);
      return;
    }

    var rm = t.closest('cart-remove-button');
    if (rm && item) {
      var b = rm.querySelector('button');
      var isLastItem = document.querySelectorAll('#CartDrawer cart-item').length <= 1;
      pending = {
        type: 'remove',
        variant: item.getAttribute('data-variant-id'),
        label: b ? (b.getAttribute('aria-label') || '') : ''
      };
      // No focus restore until Shopify confirms the removal (see settle()).
      restore = null;
      // Move focus to something that survives the redraw, so it never falls out of the dialog:
      // last item -> the cart dialog itself ("Cart, dialog"); otherwise -> "Close cart".
      setTimeout(function () {
        var target = isLastItem ? cartDialog() : cartCloseButton();
        if (target) target.focus();
      }, 0);
      return;
    }

    var add = t.closest('form[action*="/cart/add"] [type="submit"], button[name="add"]');
    if (add) {
      pending = { type: 'add', name: addedProductName(add) };
      // Adding from a product card inside the open cart: the cart is redrawn and the pressed button
      // disappears. Keep focus inside the cart ("Close cart") instead of letting it fall to the page.
      if (add.closest('#CartDrawer')) {
        restore = { kind: 'close', until: Date.now() + RESTORE_MS + SETTLE_MS + 1500 };
        setTimeout(function () {
          var close = cartCloseButton();
          if (close) close.focus();
        }, 0);
      }
    }
  }, true);

  // Typing a number into the cart quantity field. The theme also fires 'change' on this field
  // itself after a +/- press; those are ignored so focus stays on the button.
  document.addEventListener('change', function (e) {
    var inp = e.target;
    if (!inp || !inp.matches || !inp.matches('cart-item input[data-quantity-input]')) return;
    if (Date.now() - lastQtyButtonAt < 1500) return;
    var item = inp.closest('cart-item');
    pending = {
      type: 'qty',
      action: 'input',
      variant: item.getAttribute('data-variant-id'),
      prevQty: parseInt(item.getAttribute('data-quantity'), 10)
    };
    restore = { kind: 'qty', action: 'input', variant: pending.variant, until: Date.now() + RESTORE_MS + SETTLE_MS };
  }, true);

  function settle() {
    var p = pending;
    pending = null;
    if (!p) return;
    applyRestore();

    waitForCart(p).then(function (cart) {
      if (p.type === 'remove') {
        // Item gone: next item's remove button, or "Close cart" if the cart is empty.
        restore = { kind: 'remove', until: Date.now() + RESTORE_MS };
        applyRestore();
      }
      if (!cart) {
        announce(p.type === 'add' ? 'Product added to cart.' : 'Cart updated.');
        return;
      }
      var total = money(cart.total_price, cart.currency);

      if (p.type === 'qty') {
        var line = findLine(cart, p.variant);
        if (!line) { announce('Item removed from cart.' + (cart.item_count === 0 ? ' Your cart is empty.' : ' Total ' + total + '.')); return; }
        announce('Quantity ' + line.quantity + ', total ' + total + '.');
      } else if (p.type === 'remove') {
        var product = p.label.replace(/^Remove /, '').replace(/ from cart$/, '');
        announce((product ? product + ' removed from cart.' : 'Item removed from cart.') +
          (cart.item_count === 0 ? ' Your cart is empty.' : ' Total ' + total + '.'));
      } else if (p.type === 'add') {
        announce((p.name ? p.name + ' added to cart.' : 'Product added to cart.') + ' Total ' + total + '.');
      }
      applyRestore();
    });
  }

  /* ---------- Run on load and whenever the page changes ---------- */
  var scheduled = false;
  function runFixes() {
    scheduled = false;
    try {
      fixIcons();
      labelArrows();
      fixVariantPickers();
      fixMorePayment();
      fixAccordions();
      fixGallery();
      fixColourCodeAlts();
      fixCartDrawer();
      applyRestore();
      restorePickerFocus();
    } catch (err) { /* never break the page */ }
  }

  var observer = new MutationObserver(function () {
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(runFixes);
    }
    if (pending) {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        try { settle(); } catch (err) { /* never break the page */ }
      }, SETTLE_MS);
    }
  });

  // Accordion state / gallery selection changes are attribute changes, so watch those too.
  var attrObserver = new MutationObserver(function (records) {
    var acc = false, gal = false;
    for (var i = 0; i < records.length; i++) {
      if (records[i].attributeName === 'data-aria-expanded') acc = true;
      else if (records[i].target.classList && records[i].target.classList.contains('product-thumbnail-carousel__thumb')) gal = true;
    }
    try { if (acc) fixAccordions(); if (gal) fixGallery(); } catch (err) { /* never break the page */ }
  });

  function init() {
    liveRegion();
    runFixes();
    observer.observe(document.body, { childList: true, subtree: true });
    attrObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['data-aria-expanded', 'class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
